import * as React from 'react';

import type {CoreEntityType} from 'nutshell-core/types';

import {EmptyAddCard} from 'shells/card/empty-add-card';
import {colors} from 'shells/colors/colors';

import type {CreateRulesFragment, FormCreateRulesUpdateInput} from 'nutshell-graphql-types';

import {CreateRulesCardAccount} from './create-rules-card-account';
import {CreateRulesCardContact} from './create-rules-card-contact';
import {CreateRulesCardLead} from './create-rules-card-lead';
import {CreateRulesForm} from './create-rules-form';
import {CreateRulesSidebarForm} from './create-rules-sidebar-form';
import {getCreateRules, getTopLevelKey, canDeleteCreateRule} from './helpers';
import type {CreateRuleKey} from './types';

import styles from './create-rules-container.module.css';

type Props = {
    formId: string;
    hasAccountFields: boolean;
    hasContactFields: boolean;
    hasLeadFields: boolean;
    hasLeadSpecialCases: boolean;
    createRules: CreateRulesFragment;
    updateCreateRules: (arg1: FormCreateRulesUpdateInput) => Promise<any>;
};
type CreateRulesSidebar = {
    entityType: CoreEntityType | null | undefined;
    isOpen: boolean;
};

export function CreateRulesContainer(props: Props) {
    const [createRulesSidebar, setCreateRulesSidebar] = React.useState<CreateRulesSidebar>({
        entityType: null,
        isOpen: false,
    });
    const [saveFailedErrorMessage, setSaveFailedErrorMessage] = React.useState<
        string | null | undefined
    >(null);

    // @ts-expect-error
    const configuredEntityTypes = [];
    if (props.createRules.contact || props.hasContactFields) {
        configuredEntityTypes.push('contacts');
    }

    if (props.createRules.account || props.hasAccountFields) {
        configuredEntityTypes.push('accounts');
    }

    if (props.createRules.lead || props.hasLeadFields) {
        configuredEntityTypes.push('leads');
    }

    const saveCreateRules = (
        formId: string,
        createRulesKey: CreateRuleKey,
        formData: any
    ): Promise<any> => {
        const form = {
            id: formId,
            createRules: {
                [createRulesKey]: formData,
            },
        } as const;

        return props
            .updateCreateRules({
                form,
            })
            .then(() => {
                setCreateRulesSidebar({entityType: null, isOpen: false});
            })
            .catch((error) => {
                setSaveFailedErrorMessage(error.message);
            });
    };

    // We need this to be memoized so we don't try to recreate our existing create rules when saving.
    // To be specific, when a user submits changes to the create rules, the update hook (which is at a higher level)
    // will trigger a re-render of this component, and we don't want to recreate our existing create rules when that happens.
    const existingCreateRules = React.useMemo(
        () => getCreateRules(props.createRules, createRulesSidebar.entityType),
        [props.createRules, createRulesSidebar.entityType]
    );
    const isNewItem = Boolean(!existingCreateRules);

    const getAddCardText = () => {
        const createableTypes = ['contacts', 'accounts', 'leads']
            // @ts-expect-error
            .filter((type) => configuredEntityTypes.indexOf(type) === -1)
            .map((type) => {
                switch (type) {
                    case 'contacts':
                        return 'person';
                    case 'accounts':
                        return 'company';
                    case 'leads':
                        return 'lead';
                    default:
                        return '';
                }
            });

        switch (createableTypes.length) {
            case 3:
                return 'Create a person, company, or lead';
            case 2:
                return `Create a ${createableTypes[0]} or ${createableTypes[1]}`;
            case 1:
                return `Create a ${createableTypes[0]}`;
            default:
                return '';
        }
    };

    return (
        <div className={styles['create-rules-container']}>
            {configuredEntityTypes.includes('contacts') ? (
                <CreateRulesCardContact
                    onClick={() => setCreateRulesSidebar({entityType: 'contacts', isOpen: true})}
                    createRules={props.createRules.contact}
                />
            ) : undefined}
            {configuredEntityTypes.includes('accounts') ? (
                <CreateRulesCardAccount
                    onClick={() => setCreateRulesSidebar({entityType: 'accounts', isOpen: true})}
                    createRules={props.createRules.account}
                />
            ) : undefined}
            {configuredEntityTypes.includes('leads') ? (
                <CreateRulesCardLead
                    onClick={() => setCreateRulesSidebar({entityType: 'leads', isOpen: true})}
                    createRules={props.createRules.lead}
                />
            ) : undefined}
            {configuredEntityTypes.length < 3 ? (
                <div className={styles['add-create-rule-container']}>
                    <EmptyAddCard
                        onClick={() => setCreateRulesSidebar({entityType: null, isOpen: true})}
                        text={getAddCardText()}
                        background='white'
                        size='small'
                        withoutIcon={true}
                        color={colors.greyLt}
                        isLoading={false}
                    />
                </div>
            ) : undefined}
            {createRulesSidebar.isOpen ? (
                <CreateRulesSidebarForm
                    title={createRulesSidebar.entityType ? 'Edit create rule' : 'Add a create rule'}
                    onClose={() => {
                        setCreateRulesSidebar({entityType: null, isOpen: false});
                        setSaveFailedErrorMessage(null);
                    }}
                    deleteProps={
                        canDeleteCreateRule(props.createRules, createRulesSidebar.entityType)
                            ? {
                                  onDelete: () => {
                                      if (createRulesSidebar.entityType) {
                                          return saveCreateRules(
                                              props.formId,
                                              getTopLevelKey(createRulesSidebar.entityType),
                                              null
                                          ).then(() => {
                                              setCreateRulesSidebar({
                                                  entityType: null,
                                                  isOpen: false,
                                              });
                                          });
                                      }
                                  },
                                  title: 'Are you sure you want to remove this create rule?',
                                  buttonText: 'Remove create rule',
                                  message: '',
                              }
                            : undefined
                    }
                    saveFailedErrorMessage={saveFailedErrorMessage}
                    // @ts-expect-error
                    initialValues={existingCreateRules}
                    isNewItem={isNewItem}
                    // @ts-expect-error
                    onSubmit={(formData) => {
                        const createRulesKey = getTopLevelKey(
                            formData.entityType || createRulesSidebar.entityType
                        );
                        delete formData.entityType;

                        return saveCreateRules(props.formId, createRulesKey, formData);
                    }}
                >
                    <CreateRulesForm
                        formId={props.formId}
                        createRules={props.createRules}
                        hasLeadSpecialCases={props.hasLeadSpecialCases}
                        configuredEntityTypes={configuredEntityTypes}
                        startFromEntityType={createRulesSidebar.entityType}
                    />
                </CreateRulesSidebarForm>
            ) : undefined}
        </div>
    );
}
